import React from 'react'
import { GridRenderCellParams } from '@mui/x-data-grid-pro'
import { StringCell } from 'components/Shared/MuiTable/Cell/StringCell'
import i18n from 'config/i18n'
import HighlightCell from 'components/Shared/MuiTable/Cell/HighlightCell'
import { TagCell } from 'components/Shared/MuiTable/Cell/TagCell'
import { ProfileCell } from 'components/Shared/MuiTable/Cell/ProfileCell'
import { SocialMediaInfoCell } from 'components/Shared/MuiTable/Cell/SocialMediaInfoCell'
import { FIELDS_TYPE } from 'components/utils/filterAndSort'
import { GridColDefProps } from 'components/Submissions/Review/Submissions/SimpleTable/constants'
import { ProfileTypeCell } from 'components/Shared/MuiTable/Cell/ProfileTypeCell'
import { CustomEngagementHeader, getMultilineHeader } from './headers'
import { UpdateAmbassadorFavoritePayload } from 'state/ambassadors'
import { PLATFORM_CONTENT_TYPE_ENUM, PLATFORM_ENUM } from 'services/api/constants'
import { PLATFORM_CONTENT, PLATFORM_CONTENT_TYPE_ENGAGEMENT } from '../constants'
import { EngagementId } from 'state/tasks'

const getMoreEngagementCols = (contentType: PLATFORM_CONTENT_TYPE_ENUM | null, expandEngagement: boolean) => {
    const cols: GridColDefProps[] = []
    if (!expandEngagement || !contentType) return cols

    if (PLATFORM_CONTENT_TYPE_ENGAGEMENT[contentType]?.includes(EngagementId.LIKE)) {
        cols.push({
            field: FIELDS_TYPE.AVERAGE_ENGAGEMENT_LIKES,
            headerName: i18n.t('submission.table.averageLikes'),
            width: 140,
            minWidth: 140,
            sortable: false,
            align: 'right',
            renderHeader: params => getMultilineHeader({ params, width: 120 }),
            renderCell: (props: GridRenderCellParams) => <StringCell {...props} />,
            headerClassName: 'engagement-detail-metrics',
            filterable: false,
        })
    }

    if (PLATFORM_CONTENT_TYPE_ENGAGEMENT[contentType]?.includes(EngagementId.COMMENT)) {
        cols.push({
            field: FIELDS_TYPE.AVERAGE_ENGAGEMENT_COMMENTS,
            headerName: i18n.t('submission.table.averageComment'),
            width: 140,
            align: 'right',
            minWidth: 140,
            sortable: false,
            renderCell: (props: GridRenderCellParams) => <StringCell {...props} />,
            renderHeader: params => getMultilineHeader({ params, width: 120 }),
            headerClassName: 'engagement-detail-metrics',

            filterable: false,
        })
    }
    if (PLATFORM_CONTENT_TYPE_ENGAGEMENT[contentType]?.includes(EngagementId.SHARE)) {
        cols.push({
            field: FIELDS_TYPE.AVERAGE_ENGAGEMENT_SHARES,
            headerName: i18n.t('submission.table.averageShares'),
            width: 140,
            align: 'right',
            minWidth: 140,
            sortable: false,
            renderCell: (props: GridRenderCellParams) => <StringCell {...props} />,
            renderHeader: params => getMultilineHeader({ params, width: 120 }),
            headerClassName: 'engagement-detail-metrics',

            filterable: false,
        })
    }
    if (PLATFORM_CONTENT_TYPE_ENGAGEMENT[contentType]?.includes(EngagementId.SAVE)) {
        cols.push({
            field: FIELDS_TYPE.AVERAGE_ENGAGEMENT_SAVES,
            headerName: i18n.t('submission.table.averageSaves'),
            width: 140,
            minWidth: 140,
            align: 'right',
            sortable: false,
            renderCell: (props: GridRenderCellParams) => <StringCell {...props} />,
            renderHeader: params => getMultilineHeader({ params, width: 120 }),
            headerClassName: 'engagement-detail-metrics',

            filterable: false,
        })
    }
    if (PLATFORM_CONTENT_TYPE_ENGAGEMENT[contentType]?.includes(EngagementId.STICKER_TAP)) {
        cols.push({
            field: FIELDS_TYPE.NUMBER_OF_STICKER_TAPS,
            headerName: i18n.t('submission.table.averageStickerTaps'),
            width: 140,
            minWidth: 140,
            align: 'right',
            sortable: false,
            renderCell: (props: GridRenderCellParams) => <StringCell {...props} />,
            renderHeader: params => getMultilineHeader({ params, width: 120 }),
            headerClassName: 'engagement-detail-metrics',

            filterable: false,
        })
    }

    if (PLATFORM_CONTENT_TYPE_ENGAGEMENT[contentType]?.includes(EngagementId.LINK_CLICK)) {
        cols.push({
            field: FIELDS_TYPE.NUMBER_OF_LINK_CLICKS,
            headerName: i18n.t('submission.table.averageLinkClicks'),
            width: 140,
            minWidth: 140,
            align: 'right',
            sortable: false,
            renderCell: (props: GridRenderCellParams) => <StringCell {...props} />,
            renderHeader: params => getMultilineHeader({ params, width: 120 }),
            headerClassName: 'engagement-detail-metrics',

            filterable: false,
        })
    }

    if (PLATFORM_CONTENT_TYPE_ENGAGEMENT[contentType]?.includes(EngagementId.CTA_CLICK)) {
        cols.push({
            field: FIELDS_TYPE.AVERAGE_CTA_CLICKS,
            headerName: i18n.t('submission.table.averageCTAButton'),
            width: 140,
            minWidth: 140,
            align: 'right',
            sortable: false,
            renderCell: (props: GridRenderCellParams) => <StringCell {...props} />,
            renderHeader: params => getMultilineHeader({ params, width: 120 }),
            headerClassName: 'engagement-detail-metrics',

            filterable: false,
        })
    }

    if (PLATFORM_CONTENT_TYPE_ENGAGEMENT[contentType]?.includes(EngagementId.TAG)) {
        cols.push({
            field: FIELDS_TYPE.AVERAGE_TAGS,
            headerName: i18n.t('submission.table.averageTags'),
            width: 140,
            minWidth: 140,
            align: 'right',
            sortable: false,
            renderCell: (props: GridRenderCellParams) => <StringCell {...props} />,
            renderHeader: params => getMultilineHeader({ params, width: 120 }),
            headerClassName: 'engagement-detail-metrics',

            filterable: false,
        })
    }

    return cols
}

const getDynamicCols = (contentType: PLATFORM_CONTENT_TYPE_ENUM | null) => {
    const cols: GridColDefProps[] = []

    if (!contentType) return cols

    if (PLATFORM_CONTENT_TYPE_ENGAGEMENT[contentType]?.includes(EngagementId.REACH)) {
        cols.push({
            field: FIELDS_TYPE.AVERAGE_REACH,
            headerName: i18n.t('submission.table.averageReach'),
            width: 140,
            minWidth: 140,
            align: 'right',
            renderCell: (props: GridRenderCellParams) => <StringCell {...props} />,
            renderHeader: params => getMultilineHeader({ params, width: 120 }),
            sortable: false,
            filterable: false,
        })
    }

    if (PLATFORM_CONTENT_TYPE_ENGAGEMENT[contentType]?.includes(EngagementId.VIEW)) {
        cols.push({
            field: FIELDS_TYPE.AVERAGE_VIEWS,
            headerName: i18n.t('submission.table.averageViews'),
            width: 140,
            minWidth: 140,
            align: 'right',
            renderCell: (props: GridRenderCellParams) => <StringCell {...props} />,
            renderHeader: params => getMultilineHeader({ params, width: 120 }),
            sortable: false,
            filterable: false,
        })
    }
    if (PLATFORM_CONTENT_TYPE_ENGAGEMENT[contentType]?.includes(EngagementId.IMPRESSION)) {
        cols.push({
            field: FIELDS_TYPE.AVERAGE_IMPRESSIONS,
            headerName: i18n.t('submission.table.averageImpression'),
            width: 140,
            minWidth: 140,
            align: 'right',
            renderCell: (props: GridRenderCellParams) => <StringCell {...props} />,
            renderHeader: params => getMultilineHeader({ params, width: 120 }),
            sortable: false,
            filterable: false,
        })
    }

    return cols
}

const getDynamicSocialMediaSubFields = (contentType: PLATFORM_CONTENT_TYPE_ENUM | null) => {
    const subFields: {
        field: string
        headerName: string
    }[] = []

    if (!contentType) return subFields

    if (PLATFORM_CONTENT[PLATFORM_ENUM.IG].includes(contentType)) {
        subFields.push(
            ...[
                {
                    field: FIELDS_TYPE.IG_FOLLOWERS,
                    headerName: i18n.t('menu.campaignEditor.requirement.instagramFollowerHeader'),
                },
                {
                    field: FIELDS_TYPE.IG_USERNAME,
                    headerName: i18n.t('submission.table.instagramUsernameLabel'),
                },
            ],
        )
    }

    if (PLATFORM_CONTENT[PLATFORM_ENUM.FB].includes(contentType)) {
        subFields.push(
            ...[
                {
                    field: FIELDS_TYPE.FB_FRIENDS,
                    headerName: i18n.t('engagementMetric.app.facebookFriendNumber'),
                },
                {
                    field: FIELDS_TYPE.FB_USERNAME,
                    headerName: i18n.t('submission.table.facebookUsernameLabel'),
                },
            ],
        )
    }

    if (PLATFORM_CONTENT[PLATFORM_ENUM.TIKTOK].includes(contentType)) {
        subFields.push(
            ...[
                {
                    field: FIELDS_TYPE.TIKTOK_FOLLOWERS,
                    headerName: i18n.t('engagementMetric.app.tiktokNumberFollowers'),
                },
                {
                    field: FIELDS_TYPE.TIKTOK_USERNAME,
                    headerName: i18n.t('submission.table.tikTokUsernameLabel'),
                },
            ],
        )
    }

    if (PLATFORM_CONTENT[PLATFORM_ENUM.TWITTER].includes(contentType)) {
        subFields.push(
            ...[
                {
                    field: FIELDS_TYPE.TWITTER_FOLLOWERS,
                    headerName: i18n.t('submission.table.twitterFollowersLabel'),
                },
                {
                    field: FIELDS_TYPE.TWITTER_USERNAME,
                    headerName: i18n.t('submission.table.twitterUsernameLabel'),
                },
            ],
        )
    }

    return subFields
}

export const getAmbassadorColumnsDef = (
    contentType: PLATFORM_CONTENT_TYPE_ENUM | null,
    expandEngagement: boolean,
    setExpandEngagement: (v: boolean) => void,
    handleClickFavorite: (payload: UpdateAmbassadorFavoritePayload) => void,
): GridColDefProps[] => {
    return [
        {
            field: 'favorited',
            headerName: i18n.t('submission.table.favourite'),
            width: 60,
            minWidth: 60,
            cellClassName: 'favorite-cell',
            sortable: false,
            renderCell: (props: GridRenderCellParams) => {
                return (
                    <HighlightCell
                        {...props}
                        icon="star"
                        indicatorColor={props.value ? 'yolkYellow' : undefined}
                        isDisabled={false}
                        onClick={() => {
                            handleClickFavorite({
                                favorited: !props.value,
                                partiposter_uuid: props.row.profile.profile.uuid,
                                brand_uuid: props.row.brandUuid,
                                brand_name: props.row.brand_name,
                            })
                        }}
                    />
                )
            },
        },
        {
            field: 'profile',
            headerName: i18n.t('submission.table.profile'),
            width: 180,
            minWidth: 180,
            sortable: false,
            filterable: false,
            renderCell: (props: GridRenderCellParams) => <ProfileCell {...props} />,
            subFields: [
                {
                    field: 'name',
                    headerName: i18n.t('submission.table.nameLabel'),
                },
                {
                    field: 'age',
                    headerName: i18n.t('submission.table.ageLabel'),
                },
                {
                    field: 'gender',
                    headerName: i18n.t('submission.table.genderLabel'),
                },
            ],
        },
        {
            field: 'profile_tier',
            headerName: i18n.t('submission.table.profileType'),
            width: 100,
            minWidth: 100,
            sortable: false,
            filterable: false,
            align: 'center',
            renderCell: (props: GridRenderCellParams) => <ProfileTypeCell {...props} />,
            renderHeader: params => getMultilineHeader({ params, width: 120 }),
        },
        {
            field: 'socialMediaInfo',
            headerName: i18n.t('submission.table.socialMediaInfo'),
            type: 'string',
            width: 180,
            minWidth: 180,
            sortable: false,
            renderCell: (props: GridRenderCellParams) => <SocialMediaInfoCell {...props} />,
            subFields: getDynamicSocialMediaSubFields(contentType),
        },
        {
            field: 'partiposterTags',
            headerName: i18n.t('submission.table.partiposterTagsLabel'),
            width: 280,
            minWidth: 280,
            renderCell: (props: GridRenderCellParams) => <TagCell {...props} />,
            sortable: false,
            filterable: false,
        },
        {
            field: FIELDS_TYPE.AMBASSADOR_BRAND,
            headerName: i18n.t('submission.table.brand'),
            width: 160,
            minWidth: 160,
            sortable: false,
            filterable: false,
            renderCell: (props: GridRenderCellParams) => {
                const value = `${props.row.brand_name} (${props.row.brand_task_count})`
                return (
                    <StringCell
                        {...props}
                        value={{
                            value: value,
                        }}
                    />
                )
            },
        },
        ...getDynamicCols(contentType),
        {
            field: FIELDS_TYPE.AVERAGE_ENGAGEMENT_RATE,
            headerName: i18n.t('submission.table.averageEngagementRate'),
            width: 200,
            minWidth: 200,
            align: 'right',
            renderCell: (props: GridRenderCellParams) => <StringCell {...props} />,
            renderHeader: params =>
                getMultilineHeader({
                    params,
                    width: (params.colDef.width ?? 200) - 48,
                    tooltipLabel: i18n.t('submission.table.averageEngagementRateTooltips'),
                }),
            sortable: false,
            filterable: false,
        },
        {
            field: FIELDS_TYPE.AVERAGE_CONTENT_ENGAGEMENT_RATE,
            headerName: i18n.t('submission.table.averageContentEngagementRate'),
            width: 200,
            minWidth: 200,
            align: 'right',
            renderCell: (props: GridRenderCellParams) => <StringCell {...props} />,
            renderHeader: params =>
                getMultilineHeader({
                    params,
                    width: (params.colDef.width ?? 200) - 48,
                    tooltipLabel: i18n.t('submission.table.averageContentEngagementRateTooltips'),
                }),
            sortable: false,
            filterable: false,
        },
        {
            field: FIELDS_TYPE.AVERAGE_ENGAGEMENT,
            headerName: i18n.t('submission.table.averageEngagement'),
            headerClassName: 'engagement-header',
            width: 140,
            minWidth: 140,
            align: 'right',
            sortable: false,
            filterable: false,
            renderHeader(params) {
                return (
                    <CustomEngagementHeader
                        expandEngagement={expandEngagement}
                        setExpandEngagement={setExpandEngagement}
                        params={params}
                        width={140}
                    />
                )
            },
            renderCell: (props: GridRenderCellParams) => <StringCell {...props} />,
        },
        ...getMoreEngagementCols(contentType, expandEngagement),
    ]
}
